import localforage from '@/lib/localforageClient';
import { queryClient, trpc } from '@/lib/trpc';

/**
 * Clear all document-related cache from localforage
 * This forces the editor to fetch fresh data from the server
 */
export async function clearDocumentCache(): Promise<number> {
  try {
    const keys = await localforage.keys();
    const docKeys = keys.filter(key => key.startsWith('doc-'));
    
    for (const key of docKeys) {
      await localforage.removeItem(key);
    }
    
    console.log(`[Cache] Cleared ${docKeys.length} cached documents`);
    return docKeys.length;
  } catch (error) {
    console.error('[Cache] Error clearing document cache:', error);
    return 0;
  }
}

/**
 * Refresh topic hierarchy and clear document cache
 * Call this after any operation that modifies the topic structure
 */
export async function refreshTopicHierarchyAndCache(workspaceId: string): Promise<void> {
  if (!workspaceId) {
    console.warn('[Cache] No workspace ID provided, skipping refresh');
    return;
  }

  // Clear document cache first
  await clearDocumentCache();
  
  // Invalidate topic hierarchy query
  await queryClient.invalidateQueries({
    queryKey: trpc.topics.getWorkspaceTopicHierarchy.queryKey({
      workspaceId: workspaceId,
    }),
  });
  
  // Also invalidate any related queries
  await queryClient.invalidateQueries({
    queryKey: [['topics']],
    predicate: (query) => {
      const queryKey = query.queryKey as any[];
      return queryKey.some(key => 
        typeof key === 'object' && 
        key?.workspaceId === workspaceId
      );
    }
  });
  
  console.log('[Cache] Topic hierarchy and cache refreshed');
}

/**
 * Clear cache for a specific document
 */
export async function clearDocumentCacheById(docId: string): Promise<void> {
  try {
    const cacheKey = `doc-${docId}`;
    documentCache.delete(cacheKey);
    await localforage.removeItem(cacheKey);
    console.log(`[Cache] Cleared cache for document ${docId}`);
  } catch (error) {
    console.error(`[Cache] Error clearing cache for document ${docId}:`, error);
  }
}
